<!--
Copyright 2021 Carnegie Mellon University. All Rights Reserved.
Released under a MIT (SEI)-style license. See LICENSE.md in the project root for license information.
-->

<div class="cssLayoutRowStartCenter">
  <div class="sp-icon">
    <mat-icon class="mdi-24px" fontIcon="mdi-clipboard-play" [ngStyle]="{ color: 'var(--mat-sys-primary)' }"
      alt="Events"></mat-icon>
  </div>
  <mat-form-field class="search-field">
    <input matInput [(ngModel)]="filterString" (keyup)="applyFilter($event.target.value)" placeholder="Search" />
    @if (filterString) {
    <button mat-icon-button matSuffix (click)="clearFilter()" title="Clear Search">
      <mat-icon style="transform: scale(0.85)" fontIcon="mdi-close-circle-outline"></mat-icon>
    </button>
    }
  </mat-form-field>
  <div class="status-selection">
    <mat-checkbox [(ngModel)]="showActive" (change)="filterAndSort()">Active</mat-checkbox>
    <mat-checkbox [(ngModel)]="showEnded" (change)="filterAndSort()">Ended</mat-checkbox>
    <mat-checkbox [(ngModel)]="showFailed" (change)="filterAndSort()">Failed</mat-checkbox>
  </div>
  <div class="nator-div">
    <mat-paginator #paginator [length]="totalRows$ | async" [pageIndex]="0" [pageSize]="20"
      [pageSizeOptions]="[5, 10, 15, 20, 25, 50, 100, 200]"></mat-paginator>
  </div>
</div>

<div class="table-container">
  <table mat-table [dataSource]="displayedRows$ | async" matSort multiTemplateDataRows>

    <ng-container matColumnDef="actions">
      <th mat-header-cell *matHeaderCellDef>&nbsp;</th>
      <td mat-cell *matCellDef="let element">
        <button mat-icon-button (click)="editEvent(element); $event.stopPropagation()" title="Edit event"
          [disabled]="!(canEdit(element.id) || canManage(element.id))">
          <mat-icon class="mdi-18px" fontIcon="mdi-square-edit-outline"></mat-icon>
        </button>
        <button mat-icon-button ngxClipboard [cbContent]="element.id" (click)="$event.stopPropagation()"
          title="Copy: {{ element.id }}">
          <mat-icon class="mdi-18px" fontIcon="mdi-content-copy"></mat-icon>
        </button>
      </td>
    </ng-container>

    <ng-container matColumnDef="name">
      <th mat-header-cell *matHeaderCellDef mat-sort-header>Name</th>
      <td mat-cell *matCellDef="let element">{{ element.name }}</td>
    </ng-container>

    <ng-container matColumnDef="username">
      <th mat-header-cell *matHeaderCellDef mat-sort-header>User</th>
      <td mat-cell *matCellDef="let element">{{ element.username }}</td>
    </ng-container>

    <ng-container matColumnDef="status">
      <th mat-header-cell *matHeaderCellDef mat-sort-header>Status</th>
      <td mat-cell *matCellDef="let element">
        {{ element.status }}
        <!-- Deliberately not the error text itself: a tooltip cannot hold multi-KB Terraform
             output. Expanding the row shows the summary and the full detail. -->
        @if (element.errorMessage) {
        <mat-icon
          class="mdi-18px error-indicator"
          fontIcon="mdi-alert-circle-outline"
          matTooltip="This event failed - expand the row to see why"
          matTooltipPosition="right">
        </mat-icon>
        }
      </td>
    </ng-container>

    <ng-container matColumnDef="statusDate">
      <th mat-header-cell *matHeaderCellDef mat-sort-header>Status Date</th>
      <td mat-cell *matCellDef="let element">{{
        !!element.statusDate ? (element.statusDate | date: 'yyyy-MM-dd HH:mm') : ' '
        }}</td>
    </ng-container>

    <ng-container matColumnDef="launchDate">
      <th mat-header-cell *matHeaderCellDef mat-sort-header>Launch Date</th>
      <td mat-cell *matCellDef="let element">{{
        !!element.launchDate ? (element.launchDate | date: 'yyyy-MM-dd HH:mm') : ' '
        }}</td>
    </ng-container>

    <ng-container matColumnDef="expirationDate">
      <th mat-header-cell *matHeaderCellDef mat-sort-header>Expiration Date</th>
      <td mat-cell *matCellDef="let element">{{
        !!element.expirationDate ? (element.expirationDate | date: 'yyyy-MM-dd HH:mm') : ' '
        }}</td>
    </ng-container>

    <ng-container matColumnDef="expandedDetail">
      <td mat-cell *matCellDef="let element" [attr.colspan]="displayedColumns.length">
        <div [@detailExpand]="element.id === expandedEventId ? 'expanded' : 'collapsed'"
          (click)="$event.stopPropagation()">
          @if (element.id === expandedEventId) {
          @if (element.errorMessage) {
          <!-- Text and the error system token only - design-specs/angular/modals.md 1.6 rules
               out a decorative banner or accent background for a server error. -->
          <div class="error-container">
            <div class="error-heading">
              <mat-icon class="mdi-24px error-indicator" fontIcon="mdi-alert-circle-outline"></mat-icon>
              <strong>Failure</strong>
            </div>
            <div>{{ element.errorMessage }}</div>
            @if (element.lastLaunchInternalStatus) {
            <div class="error-meta">
              Failed at: {{ element.lastLaunchInternalStatus }}
            </div>
            }
            @if (element.workspaceId) {
            <div class="error-ids">
              <div class="error-id-row">
                <button mat-icon-button class="error-copy-button" ngxClipboard
                  [cbContent]="element.workspaceId"
                  (cbOnSuccess)="snackBar.open('Copied to clipboard', 'Dismiss')"
                  (click)="$event.stopPropagation()" title="Copy Workspace ID">
                  <mat-icon class="mdi-18px" fontIcon="mdi-content-copy"></mat-icon>
                </button>
                <span>Workspace: {{ element.workspaceId }}</span>
              </div>
              @if (element.runId) {
              <div class="error-id-row">
                <button mat-icon-button class="error-copy-button" ngxClipboard [cbContent]="element.runId"
                  (cbOnSuccess)="snackBar.open('Copied to clipboard', 'Dismiss')"
                  (click)="$event.stopPropagation()" title="Copy Run ID">
                  <mat-icon class="mdi-18px" fontIcon="mdi-content-copy"></mat-icon>
                </button>
                <span>Run: {{ element.runId }}</span>
              </div>
              }
            </div>
            <!-- Caster UI has no per-workspace route yet, so this is a link to Caster itself;
                 without a configured address there is nowhere to send the operator. -->
            @if (casterUIAddress) {
            <div class="error-meta">
              <a [href]="casterUIAddress" target="_blank" rel="noopener">
                Open Caster
                <mat-icon class="mdi-18px error-link-icon" fontIcon="mdi-open-in-new"></mat-icon>
              </a>
            </div>
            }
            }
            @if (loadingErrorDetailFor === element.id) {
            <div class="error-meta">Loading details...</div>
            }
            @if (errorDetail(element.id); as detail) {
            <pre class="error-detail">{{ detail }}</pre>
            <button matButton="outlined" class="error-copy-detail" ngxClipboard [cbContent]="detail"
              (cbOnSuccess)="snackBar.open('Copied to clipboard', 'Dismiss')"
              (click)="$event.stopPropagation()">
              <mat-icon class="mdi-18px" fontIcon="mdi-content-copy"></mat-icon>
              Copy Details
            </button>
            }
          </div>
          }
          <div class="memberships-container">
            <label class="memberships-label">Memberships</label>
            <app-event-memberships [event]="element" [embedded]="false"></app-event-memberships>
          </div>
          }
        </div>
      </td>
    </ng-container>

    <tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></tr>
    <tr mat-row *matRowDef="let row; columns: displayedColumns" class="element-row"
      (click)="selectEvent(row.id)"></tr>
    <tr mat-row *matRowDef="let row; columns: ['expandedDetail']" class="detail-row"></tr>
  </table>
</div>

@if (isLoading) {
<mat-card appearance="outlined" style="display: flex; justify-content: center; align-items: center; width: 100%">
  <mat-progress-spinner color="primary" mode="indeterminate">
  </mat-progress-spinner>
</mat-card>
}
